import {
  Condition,
  ConditionType,
  CoolingOffDetails,
  CoolingOffType,
  DayType,
  Deposit,
  DepositType,
  FormCondition,
  NoCoolingOffReason,
  StaffGetContractDatesResponse,
  StaffGetContractDepositsResponse,
  StaffGetContractSettlementResponse,
  StaffGetSaleInformationResponse,
  StaffUpdateContractDatesRequest,
  StaffUpdateContractDepositsRequest,
  StaffUpdateContractSettlementRequest,
  StaffUpdateSaleInformationRequest,
  TimeUnit,
  useInvalidateUseStaffGetContractDates,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractSettlement,
  useInvalidateUseStaffGetSaleInformation,
  useStaffGetContractDates,
  useStaffGetContractDeposits,
  useStaffGetContractSettlement,
  useStaffGetSaleInformation,
  useStaffUpdateContractDates,
  useStaffUpdateContractDeposits,
  useStaffUpdateContractSettlement,
  useStaffUpdateSaleInformation,
  ValueType,
} from '@urbanx/agx.campaigns.client';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TaskCategoryTaskType } from '@urbanx/agx.activities.client';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';

type DepositPayableOption = 'quarter' | 'full';
const defaultDepositPayableOption: DepositPayableOption = 'quarter';

const toContractDateRequest = (value?: Date): StaffUpdateContractDatesRequest['contractDate'] =>
  toDateOnlyString(value) as StaffUpdateContractDatesRequest['contractDate'];

const exchangeInformationTaskTypes = [
  TaskCategoryTaskType.SolicitorExchangeInformation,
  TaskCategoryTaskType.AgentExchangeInformation,
  TaskCategoryTaskType.ContractExchangeInformation,
];

const exchangeContractDatesTaskTypes = [
  TaskCategoryTaskType.SolicitorExchangeContractDates,
  TaskCategoryTaskType.AgentExchangeContractDates,
  TaskCategoryTaskType.ContractExchangeContractDates,
];

export interface ExchangeTaskFormState {
  contractDate?: Date;
  salePrice?: number;
  deposit?: Deposit;
  depositPayableOption?: DepositPayableOption;
  urbanXNoted?: boolean;
  coolingOffType?: CoolingOffType;
  customCoolingOffDays?: number;
  noCoolingOffReason?: NoCoolingOffReason;
  subjectToFinance?: FormCondition;
  settlementInfo?: FormCondition;
  specialConditionsReviewed: boolean;
}

const createInitialExchangeTaskFormState = (): ExchangeTaskFormState => ({
  settlementInfo: {
    condition: Condition.NdaysFromDate,
    fromDateTimeUnit: TimeUnit.Days,
  },
  specialConditionsReviewed: false,
});

const buildDeposits = (state: ExchangeTaskFormState): Deposit[] | undefined => {
  const depositPercent =
    state.deposit?.depositValueType === ValueType.Percentage ? state.deposit.percent : undefined;
  const depositAmount =
    state.deposit?.depositValueType === ValueType.Dollar ? state.deposit.amount : undefined;
  const depositPayableOption = state.depositPayableOption ?? defaultDepositPayableOption;

  if (!state.deposit?.isDepositSelected || (!depositPercent && !depositAmount)) {
    return undefined;
  }

  const depositValueType = depositAmount ? ValueType.Dollar : ValueType.Percentage;

  if (depositPayableOption === 'full') {
    return [
      {
        depositType: DepositType.Initial,
        depositValueType,
        percent: depositPercent,
        amount: depositAmount,
        condition: Condition.OnDate,
        conditionType: ConditionType.Contract,
        isDepositSelected: true,
      },
    ];
  }

  const initialDepositPercent = 0.25;
  const initialDepositAmount = state.salePrice
    ? state.salePrice * (initialDepositPercent / 100)
    : undefined;
  if (depositAmount !== undefined && initialDepositAmount === undefined) {
    return undefined;
  }

  const balancePercent =
    depositPercent !== undefined ? depositPercent - initialDepositPercent : undefined;
  const balanceAmount =
    depositAmount !== undefined && initialDepositAmount !== undefined
      ? depositAmount - initialDepositAmount
      : undefined;

  return [
    {
      depositType: DepositType.Initial,
      depositValueType: ValueType.Percentage,
      percent: initialDepositPercent,
      amount: undefined,
      condition: Condition.OnDate,
      conditionType: ConditionType.Contract,
      isDepositSelected: true,
    },
    {
      depositType: DepositType.Balance,
      depositValueType,
      percent: balancePercent !== undefined && balancePercent > 0 ? balancePercent : undefined,
      amount: balanceAmount !== undefined && balanceAmount > 0 ? balanceAmount : undefined,
      condition: Condition.OnDate,
      conditionType: ConditionType.Contract,
      isDepositSelected: true,
    },
  ].filter((deposit) => deposit.percent !== undefined || deposit.amount !== undefined);
};

const buildCoolingOffDetails = (state: ExchangeTaskFormState): CoolingOffDetails | undefined => {
  if (!state.coolingOffType) return undefined;

  return {
    coolingOffType: state.coolingOffType,
    coolingOffDays:
      state.coolingOffType === CoolingOffType.NDays ? state.customCoolingOffDays : undefined,
    noCoolingOffReason:
      state.coolingOffType === CoolingOffType.NoCoolingOff ? state.noCoolingOffReason : undefined,
  };
};

const buildSubjectToFinance = (state: ExchangeTaskFormState): FormCondition | undefined => {
  if (!state.subjectToFinance?.condition) return undefined;

  return {
    ...state.subjectToFinance,
    conditionType: ConditionType.Finance,
  };
};

const buildSettlementInfo = (state: ExchangeTaskFormState): FormCondition | undefined => {
  const settlementInfo = state.settlementInfo;

  if (settlementInfo?.condition === Condition.OnSpecificDate) {
    if (!settlementInfo.referenceDate) return undefined;

    return {
      ...settlementInfo,
      condition: Condition.OnSpecificDate,
      conditionType: ConditionType.Contract,
    };
  }

  if (settlementInfo?.condition !== Condition.NdaysFromDate || !settlementInfo.daysFromDate) {
    return undefined;
  }

  return {
    ...settlementInfo,
    condition: Condition.NdaysFromDate,
    conditionType: ConditionType.Contract,
    dayType: DayType.Days,
    fromDateTimeUnit: settlementInfo.fromDateTimeUnit ?? TimeUnit.Days,
  };
};

export const useExchangeTaskForm = (
  campaignId: string | undefined,
  contractId: string | undefined,
  enabled: boolean
) => {
  const [state, setState] = useState<ExchangeTaskFormState>(createInitialExchangeTaskFormState);
  const [hydratedDataKey, setHydratedDataKey] = useState<string | undefined>();
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const shouldFetch = !!campaignId && !!contractId && enabled;
  const { data: saleInformation, isPending: saleInformationPending } = useStaffGetSaleInformation(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractDates, isPending: contractDatesPending } = useStaffGetContractDates(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractDeposits, isPending: depositsPending } = useStaffGetContractDeposits(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const { data: contractSettlement, isPending: settlementPending } = useStaffGetContractSettlement(
    campaignId ?? '',
    contractId ?? '',
    shouldFetch
  );
  const saleInformationFetching = useIsFetching({
    queryKey: ['staffGetSaleInformation', campaignId ?? '', contractId ?? ''],
  });
  const contractDatesFetching = useIsFetching({
    queryKey: ['staffGetContractDates', campaignId ?? '', contractId ?? ''],
  });
  const contractDepositsFetching = useIsFetching({
    queryKey: ['staffGetContractDeposits', campaignId ?? '', contractId ?? ''],
  });
  const contractSettlementFetching = useIsFetching({
    queryKey: ['staffGetContractSettlement', campaignId ?? '', contractId ?? ''],
  });
  const isFetchingInitialData =
    shouldFetch &&
    (saleInformationPending ||
      contractDatesPending ||
      depositsPending ||
      settlementPending ||
      saleInformationFetching > 0 ||
      contractDatesFetching > 0 ||
      contractDepositsFetching > 0 ||
      contractSettlementFetching > 0);

  const invalidateSaleInformation = useInvalidateUseStaffGetSaleInformation();
  const invalidateContractDates = useInvalidateUseStaffGetContractDates();
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const queryClient = useQueryClient();

  const { staffUpdateSaleInformation } = useStaffUpdateSaleInformation({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDates } = useStaffUpdateContractDates({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  });
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    if (!shouldFetch) {
      setHydratedDataKey(undefined);
      setHasLocalChanges(false);
      setState(createInitialExchangeTaskFormState());
    }
  }, [shouldFetch]);

  useEffect(() => {
    const dataKey = JSON.stringify({
      contractDates,
      contractDeposits,
      contractId,
      contractSettlement,
      saleInformation,
    });

    if (!shouldFetch || isFetchingInitialData || hydratedDataKey === dataKey || hasLocalChanges)
      return;

    const initialDeposit = contractDeposits?.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Initial
    );
    const balanceDeposit = contractDeposits?.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Balance
    );
    const totalDepositPercent = (initialDeposit?.percent ?? 0) + (balanceDeposit?.percent ?? 0);
    const initialDepositAmount =
      initialDeposit?.amount ??
      (saleInformation?.salePrice && initialDeposit?.percent
        ? saleInformation.salePrice * (initialDeposit.percent / 100)
        : 0);
    const balanceDepositAmount =
      balanceDeposit?.amount ??
      (saleInformation?.salePrice && balanceDeposit?.percent
        ? saleInformation.salePrice * (balanceDeposit.percent / 100)
        : 0);
    const totalDepositAmount = initialDepositAmount + balanceDepositAmount;
    const hasDollarDeposit =
      initialDeposit?.depositValueType === ValueType.Dollar ||
      balanceDeposit?.depositValueType === ValueType.Dollar ||
      !!initialDeposit?.amount ||
      !!balanceDeposit?.amount;
    const deposit: Deposit | undefined =
      hasDollarDeposit && totalDepositAmount > 0
        ? {
            depositType: DepositType.Initial,
            depositValueType: ValueType.Dollar,
            amount: totalDepositAmount,
            isDepositSelected: true,
          }
        : totalDepositPercent === 10 || totalDepositPercent === 5
          ? {
              depositType: DepositType.Initial,
              depositValueType: ValueType.Percentage,
              percent: totalDepositPercent,
              isDepositSelected: true,
            }
          : undefined;

    setState({
      ...createInitialExchangeTaskFormState(),
      contractDate: dateOnlyToDate(contractDates?.contractDate) ?? undefined,
      coolingOffType: contractDates?.coolingOffDetails?.coolingOffType,
      customCoolingOffDays: contractDates?.coolingOffDetails?.coolingOffDays,
      noCoolingOffReason: contractDates?.coolingOffDetails?.noCoolingOffReason,
      salePrice: saleInformation?.salePrice ?? undefined,
      deposit,
      depositPayableOption:
        initialDeposit && !balanceDeposit ? 'full' : balanceDeposit ? 'quarter' : undefined,
      subjectToFinance: contractDates?.subjectToFinance,
      settlementInfo:
        contractSettlement?.settlementInfo ?? createInitialExchangeTaskFormState().settlementInfo,
    });
    setHydratedDataKey(dataKey);
    setHasLocalChanges(false);
  }, [
    contractDates,
    contractDeposits,
    contractSettlement,
    contractId,
    hasLocalChanges,
    hydratedDataKey,
    isFetchingInitialData,
    saleInformation,
    shouldFetch,
  ]);

  const updateState = (updates: Partial<ExchangeTaskFormState>) => {
    setHasLocalChanges(true);
    setState((previous) => ({
      ...previous,
      ...updates,
    }));
  };

  const saveInformationPage = async () => {
    if (!campaignId || !contractId) return;

    const saleRequest: StaffUpdateSaleInformationRequest = {
      salePrice: state.salePrice,
      displaySalePrice: saleInformation?.displaySalePrice ?? false,
    };

    const datesRequest: StaffUpdateContractDatesRequest = {
      contractDate: toContractDateRequest(state.contractDate),
      coolingOffDetails: contractDates?.coolingOffDetails,
      waiverOfCoolingOff: contractDates?.waiverOfCoolingOff,
      subjectToFinance: contractDates?.subjectToFinance,
      buildingAndPestCondition: contractDates?.buildingAndPestCondition,
      bodyCorporateRecordsCondition: contractDates?.bodyCorporateRecordsCondition,
    };

    const depositsRequest: StaffUpdateContractDepositsRequest = {
      deposits: buildDeposits(state),
    };

    await staffUpdateSaleInformation({
      campaignId,
      contractId,
      body: saleRequest,
    });
    await staffUpdateContractDates({
      contractId,
      body: datesRequest,
    });
    await staffUpdateContractDeposits({
      contractId,
      body: depositsRequest,
    });

    if (!campaignId) return;

    await invalidateSaleInformation(campaignId, contractId);
    await invalidateContractDates(campaignId, contractId);
    await invalidateContractDeposits(campaignId, contractId);

    queryClient.setQueryData<StaffGetSaleInformationResponse>(
      ['staffGetSaleInformation', campaignId, contractId],
      {
        ...saleInformation,
        salePrice: saleRequest.salePrice,
        displaySalePrice: saleRequest.displaySalePrice,
      }
    );
    queryClient.setQueryData<StaffGetContractDatesResponse>(
      ['staffGetContractDates', campaignId, contractId],
      {
        ...contractDates,
        ...datesRequest,
      }
    );
    queryClient.setQueryData<StaffGetContractDepositsResponse>(
      ['staffGetContractDeposits', campaignId, contractId],
      {
        ...contractDeposits,
        deposits: depositsRequest.deposits ?? contractDeposits?.deposits,
      }
    );
    setHasLocalChanges(false);
  };

  const saveContractDatesPage = async () => {
    if (!campaignId || !contractId) return;

    const datesRequest: StaffUpdateContractDatesRequest = {
      contractDate: toContractDateRequest(state.contractDate),
      coolingOffDetails: buildCoolingOffDetails(state),
      waiverOfCoolingOff: contractDates?.waiverOfCoolingOff,
      subjectToFinance: buildSubjectToFinance(state),
      buildingAndPestCondition: contractDates?.buildingAndPestCondition,
      bodyCorporateRecordsCondition: contractDates?.bodyCorporateRecordsCondition,
    };

    const settlementRequest: StaffUpdateContractSettlementRequest = {
      settlementInfo: buildSettlementInfo(state),
      signboardRemoval: contractSettlement?.signboardRemoval,
    };

    await staffUpdateContractDates({
      contractId,
      body: datesRequest,
    });
    await staffUpdateContractSettlement({
      campaignId,
      contractId,
      body: settlementRequest,
    });

    if (!campaignId) return;

    await invalidateContractDates(campaignId, contractId);
    await invalidateContractSettlement(campaignId, contractId);

    queryClient.setQueryData<StaffGetContractDatesResponse>(
      ['staffGetContractDates', campaignId, contractId],
      {
        ...contractDates,
        ...datesRequest,
      }
    );
    queryClient.setQueryData<StaffGetContractSettlementResponse>(
      ['staffGetContractSettlement', campaignId, contractId],
      {
        ...contractSettlement,
        settlementInfo: settlementRequest.settlementInfo,
        signboardRemoval: settlementRequest.signboardRemoval,
      }
    );
    setHasLocalChanges(false);
  };

  const saveTask = async (taskType?: TaskCategoryTaskType) => {
    if (!taskType) return;

    if (exchangeInformationTaskTypes.includes(taskType)) {
      await saveInformationPage();
      return;
    }

    if (exchangeContractDatesTaskTypes.includes(taskType)) {
      await saveContractDatesPage();
    }
  };

  const saveTasks = async (taskTypes: (TaskCategoryTaskType | undefined)[]) => {
    if (taskTypes.some((taskType) => taskType && exchangeInformationTaskTypes.includes(taskType))) {
      await saveInformationPage();
    }

    if (
      taskTypes.some((taskType) => taskType && exchangeContractDatesTaskTypes.includes(taskType))
    ) {
      await saveContractDatesPage();
    }
  };

  return {
    isPending: isFetchingInitialData || (shouldFetch && hydratedDataKey === undefined),
    saveTask,
    saveTasks,
    state,
    updateState,
  };
};
